import { useEffect, useMemo } from 'react';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import WhyUsSection from './components/WhyUsSection';
import VomGruenderSection from './components/VomGruenderSection';
import ServicesSection from './components/ServicesSection';
import AboutSection from './components/AboutSection';
import TestimonialsSection from './components/TestimonialsSection';
import GoogleReviewSection from './components/GoogleReviewSection';
import SatisfactionSection from './components/SatisfactionSection';
import FAQSection from './components/FAQSection';
import LocationSection from './components/LocationSection';
import LeistungsgebieteSection from './components/LeistungsgebieteSection';
import CTASection from './components/CTASection';
import Footer from './components/Footer';
import SEO from '../../components/feature/SEO';
import JsonLd from '../../components/feature/JsonLd';

export default function HomePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://igm-baudienste.com';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jsonLdData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': ['LocalBusiness', 'CleaningService', 'HomeAndConstructionBusiness'],
        '@id': `${siteUrl}/#business`,
        'name': 'IGM Facility Service',
        'legalName': 'IGM Facility Service',
        'url': siteUrl,
        'telephone': '+4921027398070',
        'email': 'kontakt@igmfacilityservice.de',
        'address': {
          '@type': 'PostalAddress',
          'streetAddress': 'Industriestr. 37',
          'addressLocality': 'Ratingen',
          'postalCode': '40878',
          'addressRegion': 'Nordrhein-Westfalen',
          'addressCountry': 'DE',
        },
        'geo': {
          '@type': 'GeoCoordinates',
          'latitude': 51.2954,
          'longitude': 6.8497,
        },
        'hasMap': 'https://maps.google.com/?q=Industriestr.+37,+40878+Ratingen',
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': '5.0',
          'reviewCount': '15',
          'bestRating': '5',
          'worstRating': '1',
        },
        'openingHoursSpecification': [
          {
            '@type': 'OpeningHoursSpecification',
            'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            'opens': '10:00',
            'closes': '15:00',
          },
          {
            '@type': 'OpeningHoursSpecification',
            'dayOfWeek': 'Saturday',
            'opens': '12:00',
            'closes': '15:00',
          },
        ],
        'description': 'IGM Facility Service in Ratingen \u2013 Ihr zuverl\u00e4ssiger Partner f\u00fcr Geb\u00e4udereinigung, Facility Management, Garten- & Landschaftsbau sowie Malerarbeiten.',
        'image': `${siteUrl}/og-image.jpg`,
        'logo': 'https://storage.readdy-site.link/project_files/702188f6-12ce-4494-8584-94b61744a40b/3692de32-97b2-479c-bccc-f9e718571af7_IGM-Facility-Service-Logo.svg?v=3dccbd31b62fdc2dc6de09093e9efa22',
        'priceRange': '\u20ac\u20ac',
        'currenciesAccepted': 'EUR',
        'paymentAccepted': 'Cash, Invoice',
        'areaServed': [
          { '@type': 'City', 'name': 'Ratingen' },
          { '@type': 'City', 'name': 'D\u00fcsseldorf' },
          { '@type': 'City', 'name': 'M\u00f6nchengladbach' },
          { '@type': 'City', 'name': 'Wuppertal' },
          { '@type': 'City', 'name': 'Hagen' },
          { '@type': 'City', 'name': 'Mettmann' },
          { '@type': 'City', 'name': 'Velbert' },
          { '@type': 'City', 'name': 'Solingen' },
          { '@type': 'City', 'name': 'Remscheid' },
          { '@type': 'City', 'name': 'Erkrath' },
          { '@type': 'City', 'name': 'Hilden' },
          { '@type': 'City', 'name': 'Langenfeld' },
        ],
        'sameAs': [
          'https://maps.google.com/?q=IGM+Facility+Service+Ratingen',
        ],
        'knowsAbout': [
          'Geb\u00e4udereinigung',
          'Facility Management',
          'Garten- und Landschaftsbau',
          'Malerarbeiten',
          'Winterdienst',
          'Hausmeisterservice',
          'Sanierung',
        ],
      },
      {
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        'url': siteUrl,
        'name': 'IGM Facility Service',
        'description': 'Geb\u00e4udereinigung & Facility Management in Ratingen und NRW',
        'publisher': {
          '@id': `${siteUrl}/#business`,
        },
      },
      {
        '@type': 'FAQPage',
        '@id': `${siteUrl}/#faq`,
        'mainEntity': [
          {
            '@type': 'Question',
            'name': 'In welchen Gebieten f\u00fchrt IGM Facility Service Projekte durch?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Wir sind haupts\u00e4chlich in Ratingen und der gesamten Region NRW t\u00e4tig \u2013 darunter D\u00fcsseldorf, Mettmann, Erkrath, Hilden, Langenfeld, Velbert und weitere St\u00e4dte. Bei gr\u00f6\u00dferen Bauprojekten fahren wir auch dar\u00fcber hinaus.',
            },
          },
          {
            '@type': 'Question',
            'name': 'Was versteht man unter GalaBau und welche Leistungen sind enthalten?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'GalaBau steht f\u00fcr Garten- und Landschaftsbau und umfasst alle Arbeiten rund um die Gestaltung und Anlage von Au\u00dfenbereichen: Wege und Terrassen pflastern, Gr\u00fcnfl\u00e4chen anlegen, Hecken und Bepflanzungen setzen, Teiche bauen und Z\u00e4une errichten.',
            },
          },
          {
            '@type': 'Question',
            'name': 'Wie l\u00e4uft ein Bauprojekt mit IGM Facility Service ab?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Nach Ihrer Anfrage vereinbaren wir einen kostenlosen Vor-Ort-Termin, besprechen Ihre W\u00fcnsche und erstellen ein transparentes Festpreisangebot. Nach Auftragserteilung \u00fcbernehmen wir die komplette Koordination und Bauleitung.',
            },
          },
          {
            '@type': 'Question',
            'name': 'Arbeiten Sie mit Festpreisen oder nach Aufwand?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Wir arbeiten grunds\u00e4tzlich mit transparenten Festpreisangeboten. So haben Sie von Anfang an Kostensicherheit und m\u00fcssen keine b\u00f6sen \u00dcberraschungen bef\u00fcrchten.',
            },
          },
          {
            '@type': 'Question',
            'name': 'Wie lange dauert eine typische Wohnungssanierung?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Eine Bad-Komplettsanierung dauert in der Regel 1\u20133 Wochen. Eine Wohnungsvollsanierung (ca. 80 qm) ist meist in 4\u20138 Wochen abgeschlossen.',
            },
          },
          {
            '@type': 'Question',
            'name': 'Bieten Sie auch Einzelgewerke an oder nur Komplettl\u00f6sungen?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Beides! Sie k\u00f6nnen gezielt einzelne Leistungen beauftragen oder uns als Generalunternehmer f\u00fcr Ihr Gesamtprojekt einsetzen. Wir sind flexibel und passen uns Ihren Bed\u00fcrfnissen an.',
            },
          },
          {
            '@type': 'Question',
            'name': 'Welche Garantien erhalten wir auf die ausgef\u00fchrten Arbeiten?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Alle unsere Bauleistungen unterliegen den gesetzlichen Gew\u00e4hrleistungsfristen (5 Jahre f\u00fcr Bauleistungen nach BGB \u00a7634a). Dar\u00fcber hinaus bieten wir erweiterte Herstellergarantien auf verwendete Materialien.',
            },
          },
        ],
      },
    ],
  }), [siteUrl]);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEO
        title="IGM Bau- & Sanierungsdienste"
        description="IGM Facility Service in Ratingen – Ihr zuverlässiger Partner für Gebäudereinigung, Facility Management, Garten- & Landschaftsbau sowie Malerarbeiten. Jetzt Angebot anfordern!"
        keywords="Facility Service Ratingen, Gebäudereinigung Ratingen, Hausmeisterservice NRW"
        canonical={siteUrl}
      />
      <JsonLd data={jsonLdData} id="json-ld-home" />
      <Navbar />
      <HeroSection />
      <WhyUsSection />
      <VomGruenderSection />
      <ServicesSection />
      <AboutSection />
      <TestimonialsSection />
      <GoogleReviewSection />
      <SatisfactionSection />
      <FAQSection />
      <LeistungsgebieteSection />
      <LocationSection />
      <CTASection />
      <Footer />

      {/* Back to top button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-24 right-4 sm:bottom-8 sm:right-8 w-12 h-12 sm:w-14 sm:h-14 bg-[#0D2B4E] text-white rounded-full hover:bg-[#0a2240] transition-all flex items-center justify-center z-40 cursor-pointer"
        aria-label="Nach oben"
      >
        <i className="ri-arrow-up-line text-xl sm:text-2xl" />
      </button>
    </div>
  );
}
